import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { formatDate, formatFee } from '../../utils/format';
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_COLORS } from '../../utils/bookingStatus';
import { useToast } from '../../contexts/ToastContext';
import BookingForm from '../../components/BookingForm';
import BookingEditForm from '../../components/BookingEditForm';

export default function SentBookingsSection({ bookings, onBookingsChange, events, bookingPreFill }) {
  const { addToast } = useToast();
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [editingBookingId, setEditingBookingId] = useState(null);
  // Copie locale du pré-remplissage (deep-link ?action=book) : nettoyée à la
  // fermeture du formulaire, comme l'ancien window.__bookingFormPreFill
  const [preFill, setPreFill] = useState(null);

  useEffect(() => {
    if (bookingPreFill) {
      setPreFill(bookingPreFill);
      setShowBookingForm(true);
    }
  }, [bookingPreFill]);

  async function handleBookingCreated() {
    try {
      const res = await api.get('/bookings/promoter/mine');
      onBookingsChange(res.data.bookings || []);
      setShowBookingForm(false);
      addToast('Invitation envoyée');
    } catch (err) {
      console.error('Failed to refetch bookings:', err);
    }
  }

  async function handleCancelBooking(bookingId) {
    if (!confirm('Annuler ce booking ?')) return;
    try {
      await api.patch(`/bookings/${bookingId}/cancel`);
      onBookingsChange((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: 'cancelled' } : b))
      );
      addToast('Booking annulé');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleAcceptCounterOffer(bookingId) {
    try {
      const res = await api.patch(`/bookings/${bookingId}/counter-offer/accept`);
      const updated = res.data.booking;
      onBookingsChange((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, ...updated } : b))
      );
      addToast('Contre-proposition acceptée');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  async function handleRejectCounterOffer(bookingId) {
    try {
      const res = await api.patch(`/bookings/${bookingId}/counter-offer/reject`);
      const updated = res.data.booking;
      onBookingsChange((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, ...updated } : b))
      );
      addToast('Contre-proposition refusée');
    } catch (err) {
      addToast(err.message, 'error');
    }
  }

  function handleBookingSaved(updatedBooking) {
    onBookingsChange((prev) =>
      prev.map((b) => (b.id === updatedBooking.id ? updatedBooking : b))
    );
    setEditingBookingId(null);
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <h2 className="th-section-title">Bookings Envoyés</h2>
        <button
          onClick={() => setShowBookingForm(!showBookingForm)}
          className="th-btn-accent"
        >
          Inviter un artiste
        </button>
      </div>

      {showBookingForm && (
        <div className="mb-6 border th-border p-6 th-bg-surface">
          <BookingForm
            key={preFill?.artistId}
            events={events}
            onSuccess={handleBookingCreated}
            onCancel={() => {
              setShowBookingForm(false);
              setPreFill(null);
            }}
            preFilledArtistId={preFill?.artistId}
            preFilledArtistName={preFill?.artistName}
          />
        </div>
      )}

      {bookings.length === 0 ? (
        <div className="border th-border p-6 text-center th-text-secondary text-sm th-bg-surface">
          Aucun booking envoyé. Parcourez les artistes pour en inviter.
        </div>
      ) : (
        <div className="space-y-px dark:bg-dark-border bg-light-border">
          {bookings.map((booking) => (
            <div key={booking.id}>
              {editingBookingId === booking.id ? (
                <BookingEditForm
                  booking={booking}
                  onSaved={handleBookingSaved}
                  onCancel={() => setEditingBookingId(null)}
                />
              ) : (
                <div className="th-bg p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-mono font-medium th-text text-sm">
                        {booking.artistName || 'Artiste'} · {booking.eventTitle || 'Événement'}
                      </p>
                      <Link
                        to={`/bookings/${booking.id}?tab=messages`}
                        className="text-[10px] font-mono text-accent hover:opacity-70 transition-opacity duration-150"
                      >
                        Messages →
                      </Link>
                    </div>
                    <p className="text-xs th-text-muted font-mono mt-0.5">{formatDate(booking.eventDate)}</p>
                    {booking.proposedFee && (
                      <p className="text-xs th-text-secondary font-mono mt-1">
                        Cachet : {formatFee(booking.proposedFee, booking.currency)}
                      </p>
                    )}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {booking.travelIncluded && (
                        <span className="th-badge border-accent text-accent bg-accent/10 text-[10px]">Voyage</span>
                      )}
                      {booking.accommodationIncluded && (
                        <span className="th-badge border-accent text-accent bg-accent/10 text-[10px]">Hébergement</span>
                      )}
                    </div>
                    {booking.status === 'accepted' && booking.acceptedFee && booking.acceptedFee !== booking.proposedFee && (
                      <p className="text-xs th-text-secondary font-mono mt-1">
                        Cachet accepté : {formatFee(booking.acceptedFee, booking.currency)}
                      </p>
                    )}
                    {booking.status === 'counter_offer' && booking.acceptedFee != null && (
                      <p className="text-xs text-status-pending font-mono mt-1">
                        Cachet demandé : {formatFee(booking.acceptedFee, booking.currency)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`th-badge ${BOOKING_STATUS_COLORS[booking.status]}`}>
                      {BOOKING_STATUS_LABELS[booking.status]}
                    </span>
                    {booking.status === 'counter_offer' && (
                      <>
                        <button
                          onClick={() => handleAcceptCounterOffer(booking.id)}
                          className="th-btn-accent text-xs"
                        >
                          Accepter le cachet
                        </button>
                        <button
                          onClick={() => handleRejectCounterOffer(booking.id)}
                          className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                        >
                          Refuser
                        </button>
                      </>
                    )}
                    {booking.status === 'pending' && (
                      <button
                        onClick={() => setEditingBookingId(booking.id)}
                        className="th-btn text-xs"
                      >
                        Modifier
                      </button>
                    )}
                    {(booking.status === 'pending' || booking.status === 'accepted') && (
                      <button
                        onClick={() => handleCancelBooking(booking.id)}
                        className="border border-status-rejected/30 text-status-rejected text-xs font-mono uppercase tracking-widest px-3 py-1.5 hover:bg-status-rejected/10 transition-colors duration-150 active:opacity-70"
                      >
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
